import {useCallback} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import {
  useNewSessionApprovalMode,
  useStoredMode,
  type NewSessionConfiguration,
} from '@github-ui/one-copilot-composer/new'
import {CLIModePicker} from '../components/task-view/CLIModePicker'

export type ComposerModeControls = Pick<TaskComposerProps, 'agentMode' | 'approvalMode' | 'modePicker'>

export type NewSessionModeControls = Pick<
  NewSessionConfiguration,
  'mode' | 'onModeChange' | 'approvalMode' | 'onApprovalModeChange' | 'modePicker'
>

/**
 * Owns the agent-mode and tool-permission selection for a new-session composer and
 * renders the picker that exposes them. Every field is `undefined` while the
 * independent mode controls flag is off, so hosts keep the legacy persisted
 * permission selector.
 */
export function useComposerModeControls(options?: {portalContainerName?: string}): ComposerModeControls {
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const [selectedMode, setSelectedMode] = useStoredMode()
  const {approvalMode, selectApprovalMode} = useNewSessionApprovalMode()
  const handleModeChange = useCallback((mode: AgentMode) => setSelectedMode(mode), [setSelectedMode])
  const portalContainerName = options?.portalContainerName
  const renderModePicker = useCallback<NonNullable<TaskComposerProps['modePicker']>>(
    ({availableModes, supportsApprovalMode}) => (
      <CLIModePicker
        selectedMode={selectedMode}
        onModeChange={handleModeChange}
        availableModes={availableModes}
        selectedApprovalMode={supportsApprovalMode ? approvalMode : undefined}
        onApprovalModeChange={supportsApprovalMode ? selectApprovalMode : undefined}
        {...(portalContainerName ? {portalContainerName} : {})}
      />
    ),
    [approvalMode, handleModeChange, portalContainerName, selectApprovalMode, selectedMode],
  )

  // Callers spread the result, so the keys stay present (and explicitly undefined)
  // to keep the composer's prop shape stable when the flag is off.
  if (!independentModeControlsEnabled) return {agentMode: undefined, approvalMode: undefined, modePicker: undefined}

  return {agentMode: selectedMode, approvalMode, modePicker: renderModePicker}
}

/**
 * The same selection for the shared `NewCopilotComposer`, matching `/copilot`:
 * the stored agent mode is always offered, and tool permissions join it only
 * with the independent mode controls flag. The composer narrows both to what
 * the selected destination supports.
 */
export function useNewSessionModeControls(): NewSessionModeControls {
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const [selectedMode, setSelectedMode] = useStoredMode()
  const {approvalMode, selectApprovalMode} = useNewSessionApprovalMode()
  const renderModePicker = useCallback<NonNullable<NewSessionConfiguration['modePicker']>>(
    ({availableModes, approval}) => (
      <CLIModePicker
        selectedMode={selectedMode}
        onModeChange={setSelectedMode}
        availableModes={availableModes}
        selectedApprovalMode={approval?.selectedMode}
        availableApprovalModes={approval?.availableModes}
        onApprovalModeChange={approval?.onModeChange}
      />
    ),
    [selectedMode, setSelectedMode],
  )

  return {
    mode: selectedMode,
    onModeChange: setSelectedMode,
    ...(independentModeControlsEnabled ? {approvalMode, onApprovalModeChange: selectApprovalMode} : {}),
    modePicker: renderModePicker,
  }
}
